import React from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertTriangle } from 'lucide-react';

const Disclaimers = () => {
  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb items={[{ label: 'Disclaimers', path: '/disclaimers' }]} />

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Disclaimers</h1>
          <p className="text-xl text-gray-300">
            Important legal information you need to understand before using this educational resource.
          </p>
        </div>

        {/* Critical Alert */}
        <Alert className="mb-8 border-red-500/50 bg-red-500/10">
          <AlertTriangle className="h-6 w-6 text-red-500" />
          <AlertDescription className="text-gray-300 ml-2 text-base">
            <strong className="text-white text-lg">Education Only — Not Financial Advice</strong>
            <p className="mt-2">This website provides educational content about options trading strategies. It is NOT financial, investment, tax, or legal advice. Do not make trading decisions based solely on this material.</p>
          </AlertDescription>
        </Alert>

        {/* Sections */}
        <div className="space-y-8">
          {/* Educational Purpose */}
          <section className="glass rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Educational Purpose Only</h2>
            <div className="space-y-3 text-gray-300 leading-relaxed">
              <p>
                Options Analogies (by Datatomic Labs) is an educational resource designed to help retail investors understand options mechanics through clear analogies and plain-English explanations.
              </p>
              <p>
                <strong className="text-white">We do not:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide personalized investment advice or recommendations</li>
                <li>Recommend specific stocks, strikes, or expiration dates</li>
                <li>Guarantee profits or trading success</li>
                <li>Act as brokers, financial advisors, or registered investment advisors</li>
                <li>Provide tax, legal, or accounting guidance</li>
              </ul>
            </div>
          </section>

          {/* Risk Disclosure */}
          <section className="glass rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Risk Disclosure</h2>
            <div className="space-y-3 text-gray-300 leading-relaxed">
              <p className="text-lg font-semibold text-red-400">
                Options trading involves substantial risk and may result in loss of principal.
              </p>
              <p>Key risks include but are not limited to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Capital Loss:</strong> You may lose all capital allocated to options positions</li>
                <li><strong className="text-white">Assignment Risk:</strong> Sellers can be assigned at any time, forcing them to fulfill obligations</li>
                <li><strong className="text-white">Market Risk:</strong> Sudden price movements, gaps, and volatility can cause significant losses</li>
                <li><strong className="text-white">Liquidity Risk:</strong> Some options may have wide bid-ask spreads or difficulty closing positions</li>
                <li><strong className="text-white">Early Exercise:</strong> American-style options can be exercised before expiration</li>
                <li><strong className="text-white">Time Decay:</strong> Option values erode as expiration approaches (theta decay)</li>
              </ul>
              <p className="mt-4 text-amber-300">
                Only trade options with capital you can afford to lose. Ensure you fully understand the mechanics and risks before engaging in any options trading.
              </p>
            </div>
          </section>

          {/* No Warranty */}
          <section className="glass rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">No Warranty or Guarantee</h2>
            <div className="space-y-3 text-gray-300 leading-relaxed">
              <p>
                While we strive for accuracy, we make no warranties or guarantees about the completeness, accuracy, reliability, or suitability of the information provided.
              </p>
              <p>
                Educational calculators and examples are simplified demonstrations. They do not account for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Real-time market conditions</li>
                <li>Commissions and transaction fees</li>
                <li>Tax implications</li>
                <li>Slippage and execution quality</li>
                <li>Broker-specific margin requirements</li>
                <li>Dividend payments or corporate actions</li>
              </ul>
            </div>
          </section>

          {/* Consult Professionals */}
          <section className="glass rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Consult Qualified Professionals</h2>
            <div className="space-y-3 text-gray-300 leading-relaxed">
              <p>
                Before engaging in options trading or making any financial decisions:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Consult a licensed financial advisor for personalized investment guidance</li>
                <li>Speak with a qualified tax professional about tax implications in your jurisdiction</li>
                <li>Review your broker's options trading requirements and disclosures</li>
                <li>Understand your broker's assignment and exercise procedures</li>
              </ul>
            </div>
          </section>

          {/* Regulatory */}
          <section className="glass rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Regulatory Compliance</h2>
            <div className="space-y-3 text-gray-300 leading-relaxed">
              <p>
                Options trading is regulated by securities authorities in your jurisdiction. Ensure you:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Meet your broker's requirements for options approval levels</li>
                <li>Understand margin requirements and cash reserve obligations</li>
                <li>Comply with all applicable securities laws and regulations</li>
                <li>Review official options education resources (e.g., OCC's Characteristics and Risks of Standardized Options)</li>
              </ul>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section className="glass rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Limitation of Liability</h2>
            <div className="space-y-3 text-gray-300 leading-relaxed">
              <p>
                Datatomic Labs and Options Analogies shall not be liable for any losses, damages, or adverse consequences arising from:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use or misuse of information on this website</li>
                <li>Trading decisions made based on educational content</li>
                <li>Errors or omissions in content</li>
                <li>Technical issues or website downtime</li>
              </ul>
              <p className="mt-4 font-semibold text-white">
                By using this website, you acknowledge that you understand these disclaimers and accept full responsibility for your trading decisions.
              </p>
            </div>
          </section>
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-400">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Disclaimers;